import { Download, FileText } from "lucide-react";
import EmptyState from "@/components/common/EmptyState";
import { formatFileSize, formatUtcDate } from "@/utils/common.utils";
import type { DealDocument } from "@/types/deals.types";

interface PortalDocumentsCardProps {
  docs: DealDocument[];
  onOpen: (document: DealDocument) => void;
  testId?: string;
}

/**
 * The document surface shared by client and collaborator deal rooms. The
 * backend already filters the collection for the viewer; this component only
 * presents the files that caller is allowed to receive.
 */
export default function PortalDocumentsCard({ docs, onOpen, testId = "portal-documents" }: PortalDocumentsCardProps) {
  return (
    <section data-testid={testId}>
      <div className="mb-2 text-[12px] font-bold uppercase tracking-[1.2px] text-dash-muted">Shared documents</div>
      <div className="flex flex-col gap-3 rounded-2xl border border-dash-border bg-white p-4">
        {!docs.length ? (
          <EmptyState
            compact
            icon={FileText}
            title="No shared documents yet"
            description="Files your agent shares with you will appear here."
            className="rounded-none border-0 px-2"
            data-testid={`${testId}-empty`}
          />
        ) : docs.map((document) => (
          <div key={document.id} className="flex w-full items-center justify-between gap-3 rounded-xl bg-dash-page px-3 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <FileText className="size-4 shrink-0 text-dash-faint" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-dash-ink">{document.name}</div>
                <div className="mt-0.5 text-[11px] text-dash-muted">
                  {document.category || "Document"} · {formatUtcDate(document.created_at, {}, "—")} · {formatFileSize(document.size)}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpen(document)}
              className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-dash-greenDeep transition-colors hover:text-dash-sidebarDark"
              data-testid={`${testId}-open-${document.id}`}
            >
              <Download className="size-3.5" />
              Open
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
